import React from 'react';
import { useDispatch } from 'react-redux';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import ActionsMenu from '../../../components/ActionsMenu';
import PermissionElementWrapper from '../../PermissionControls/PermissionElementWrapper';
import {
  IDictionaryNamedCohortFieldsFragment,
  IScheduleFieldsFragment,
  PermissionKey,
} from '../../../utils/apollo/graphqlSchema';
import { useScheduleModalHandlers } from '../../Schedule/utils';
import { useShowBraze } from '../../../utils/hooks';
import SchedulePauseResumeMenuOption from '../../Schedule/components/SchedulePauseResumeMenuOption';
import { ModalSliceType, openModal } from '../../../redux/slices/modal';
import { DictionaryTypeEnum } from '../../../utils/interfaces';
import { SingleSelectOption } from '../../../components/Dropdown/SingleSelect';

interface IProps {
  schemaId: string;
  cohort: IDictionaryNamedCohortFieldsFragment;
  scheduleCohort?: IScheduleFieldsFragment;
}

const CohortDictionaryActionsMenu = ({
  schemaId,
  cohort,
  scheduleCohort,
}: IProps) => {
  const dispatch = useDispatch();
  const { checkIsPermitted } = usePermission();
  const isPermittedToDisableCohort = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortDelete],
    schemaId,
  });
  const isPermittedToCreateBrazeExport = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortBrazeExportCreate],
    schemaId,
  });
  const isPermittedToEditBrazeExport = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortBrazeExportEdit],
  });
  const isPermittedToDisableBrazeExport = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortBrazeExportDelete],
  });

  const isBrazeConnect = useShowBraze();

  const {
    handleOpenExecuteScheduleModal,
    handleOpenDeleteScheduleModal,
    handleOpenEditScheduleModal,
  } = useScheduleModalHandlers();

  const openExecuteScheduleModal = (schedule: IScheduleFieldsFragment) =>
    handleOpenExecuteScheduleModal(schedule);

  const openEditScheduleModal = (schedule: IScheduleFieldsFragment) =>
    handleOpenEditScheduleModal(schedule);

  const openDeleteScheduleModal = (schedule: IScheduleFieldsFragment) =>
    handleOpenDeleteScheduleModal(schedule);

  const handleOpenDeleteCohortModal = (
    cohort: IDictionaryNamedCohortFieldsFragment,
  ) => {
    if (!isPermittedToDisableCohort) {
      return;
    }
    dispatch(openModal({ type: ModalSliceType.DeleteCohort, data: cohort }));
  };

  const handleOpenCreateScheduleModal = (
    cohort: IDictionaryNamedCohortFieldsFragment,
  ) => {
    dispatch(
      openModal({
        type: ModalSliceType.CreateSchedule,
        data: cohort,
        id: cohort.id,
      }),
    );
  };

  return (
    <ActionsMenu strategy="fixed">
      {scheduleCohort && (
        <PermissionElementWrapper
          isPermitted={isPermittedToEditBrazeExport}
          customTooltip={
            scheduleCohort.paused
              ? 'Cannot execute a paused schedule.'
              : undefined
          }
        >
          <SingleSelectOption
            onClick={() => openExecuteScheduleModal(scheduleCohort)}
            iconName="refresh"
            label={'Execute Now'}
            disabled={scheduleCohort.paused || !isPermittedToEditBrazeExport}
          />
        </PermissionElementWrapper>
      )}
      {isBrazeConnect && !cohort?.version?.isDisabled && (
        <PermissionElementWrapper isPermitted={isPermittedToCreateBrazeExport}>
          <SingleSelectOption
            onClick={() => handleOpenCreateScheduleModal(cohort)}
            iconName="braze"
            label={'Export to Braze'}
            disabled={!isPermittedToCreateBrazeExport}
          />
        </PermissionElementWrapper>
      )}
      {scheduleCohort?.cron && (
        <PermissionElementWrapper isPermitted={isPermittedToEditBrazeExport}>
          <SingleSelectOption
            onClick={() => openEditScheduleModal(scheduleCohort)}
            iconName="pencil"
            label={'Edit'}
            disabled={!isPermittedToEditBrazeExport}
          />
        </PermissionElementWrapper>
      )}
      {scheduleCohort?.cron && (
        <PermissionElementWrapper isPermitted={isPermittedToEditBrazeExport}>
          <SchedulePauseResumeMenuOption
            schedule={scheduleCohort}
            dictionaryType={DictionaryTypeEnum.ScheduleCohort}
            requiredPermissionKeys={[PermissionKey.CohortBrazeExportEdit]}
          />
        </PermissionElementWrapper>
      )}
      {scheduleCohort && (
        <PermissionElementWrapper isPermitted={isPermittedToDisableBrazeExport}>
          <SingleSelectOption
            onClick={() => openDeleteScheduleModal(scheduleCohort)}
            iconName="trash"
            label={'Delete Schedule'}
            disabled={!isPermittedToDisableBrazeExport}
          />
        </PermissionElementWrapper>
      )}
      <PermissionElementWrapper isPermitted={isPermittedToDisableCohort}>
        <SingleSelectOption
          onClick={() => handleOpenDeleteCohortModal(cohort)}
          iconName="trash"
          label={'Delete Cohort'}
          disabled={!isPermittedToDisableCohort}
        />
      </PermissionElementWrapper>
    </ActionsMenu>
  );
};

export default CohortDictionaryActionsMenu;
